import GlobalStore from "@ipru-common/store/GlobalStore";

// Custom Yup validation function for date of birth (DOB)
export function dobValidator(value, range) {
  const age = getAge(value);
  // Check if age is between 18 and 60 years
  return age >= range[0] && age <= range[1];
}

// Calculate age from date of birth in format "dd/mm/yyyy"
export function getAge(value) {
  const dateParts = value?.split("/").map(Number);
  if (!dateParts) return;
  const day = dateParts[0];
  const month = dateParts[1] - 1;
  const year = dateParts[2];
  const dateValue = new Date(year, month, day);

  const today = new Date();
  if (dateValue > today) {
    return -1; // Future date
  }
  let age = today.getFullYear() - dateValue.getFullYear();

  // Adjust if the birthday hasn't occurred yet this year
  const birthdayThisYear = new Date(today.getFullYear(), dateValue.getMonth(), dateValue.getDate());
  if (today < birthdayThisYear) {
    age--;
  }

  return age;
}

export function formatDateToYYYYMMDD(value) {
  if (typeof value !== "string") return "";
  
  const parts = value.split("/");
  if (parts.length < 3) return "";

  const [day, month, year] = parts;

  if (!day || !month || !year) return "";

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

}


export function mobileNumberValidator(value) {
  const mobileRegex = /^[6-9]\d{9}$/;
  const { blacklistedContacts } = GlobalStore.getSnapshot();
  if (blacklistedContacts?.mobile.includes(value)) {
    return false;
  }
  return mobileRegex.test(value);
}

export function nriMobileNumberValidator(value, minLength) {
  const mobileRegex = new RegExp(`^\\d{${minLength},14}$`); // Minimum 6 digits, maximum 14 digits
  const { blacklistedContacts } = GlobalStore.getSnapshot();
  if (blacklistedContacts?.mobile.includes(value)) {
    return false;
  }
  return mobileRegex.test(value);
}

export function nriNomineeMobileNumberValidator(value, minLength) {
  const mobileRegex = new RegExp(`^\\d{${minLength},20}$`); // Minimum 6 digits, maximum 14 digits
  const { blacklistedContacts } = GlobalStore.getSnapshot();
  if (blacklistedContacts?.mobile.includes(value)) {
    return false;
  }
  return mobileRegex.test(value);
}

export function singleCharValidator(value) {
  return value.length > 1;
}

export function firstNameValidator(value) {
  const MAX_FIRST_NAME_LENGTH = 30;

  const nameParts = value?.trim()?.split(" ");
  nameParts.length > 1 && nameParts.pop();

  const firstName = nameParts.join(" ");

  if (firstName.length > MAX_FIRST_NAME_LENGTH) {
    return false;
  }

  const nameRegex = /^[a-zA-Z ]{1,30}$/;
  return nameRegex.test(firstName);
}

export function firstNameMinLength(value, minLength) {
  const nameParts = value?.trim()?.split(" ");
  nameParts.length > 1 && nameParts.pop();

  const firstName = nameParts.join(" ");

  if (firstName.length < minLength) {
    return false;
  }

  return true;
}

export function lastNameValidatorRequired(value) {
  const MAX_LAST_NAME_LENGTH = 30;

  const nameParts = value?.trim()?.split(" ");

  const lastName = nameParts.length > 1 ? nameParts.pop() : "";

  if (lastName?.length > MAX_LAST_NAME_LENGTH) {
    return false;
  }

  const nameRegex = /^[a-zA-Z]{1,30}$/;
  return nameRegex.test(lastName);
}

export function lastNameValidator(value) {
  const MAX_LAST_NAME_LENGTH = 30;

  const nameParts = value?.trim()?.split(" ");

  const lastName = nameParts.length > 1 ? nameParts.pop() : "";

  if (lastName?.length > MAX_LAST_NAME_LENGTH) {
    return false;
  }

  const nameRegex = /^[a-zA-Z]{0,30}$/;
  return nameRegex.test(lastName);
}

export function lastNameMinLength(value, minLength) {
  const nameParts = value?.trim()?.split(" ");

  const lastName = nameParts.length > 1 ? nameParts.pop() : "";

  if (lastName.length < minLength) {
    return false;
  }

  return true;
}

export function fullNameValidator(value) {
  const nameRegex = /^([A-Za-z]{1,30})(\s[A-Za-z]{1,30})+$/;
  return nameRegex.test(value);
}

// validate if the name has no special characters and numbers
export function alphaCharactersOnlyValidator(value) {
  const nameRegex = /^[a-zA-Z\s]+$/;
  return nameRegex.test(value);
}

export function stateValidator(value) {
  const nameRegex = /^[a-zA-Z\s&]+$/;
  return nameRegex.test(value);
}

export function alphaNoSpaceValidator(value) {
  const nameRegex = /^[a-zA-Z]+$/;
  return nameRegex.test(value);
}

// no special characters validator
export function noSpecialCharactersValidator(value) {
  const nameRegex = /^[a-zA-Z0-9 ]+$/;
  return nameRegex.test(value);
}

// no special characters validator for custom organization name
export function noSpecialCharacterOrgNameValidator(value) {
  const orgNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9/\-&,.\\']*(\s?[a-zA-Z0-9/\-&,.\\']+)*[a-zA-Z0-9]$/g;
  return orgNameRegex.test(value);
}
// no leading and trailing spaces
export function noLeadingTrailingSpacesValidator(value) {
  const spacesRegex = /^\S.*\S$|^\S$/;
  return spacesRegex.test(value);
}

// no special characters validator length can be 0
export function noSpecialCharactersValidator2(value) {
  const nameRegex = /^[a-zA-Z0-9 ]*$/;
  return nameRegex.test(value);
}

// no special characters except (&.,'-()) or whitespace allowed
export function noSpecialCharactersValidator3(value) {
  const nameRegex = /^[a-zA-Z0-9&.,'\-\s()]+$/;
  return nameRegex.test(value);
}

// validate first word with atleast 3 characters
export function firstWordMinLengthValidator(minLength = 2) {
  return (value) => {
    if (!value) return false;
    const firstWord = value.trim().split(/\s+/)[0];
    return firstWord.length >= minLength;
  };
}

// no-leading-trailing-spaces
export function noLeadingTrailingSpaces(value) {
  return Boolean(value.trim() === value);
}

// no-two consecutive spaces
export function noTwoSpaces(value) {
  return !value.includes("  ");
}

// validate if name doesn't have more than 2 same consecutive characters
export function noConsecutiveCharsValidator(value, limit = 2) {
  const consecutiveRegex = new RegExp(`(.)\\1{${limit}}`);
  return !consecutiveRegex.test(value);
}

// validate if name doesn't have more than 2 same consecutive characters
// allows multiple space
export function noConsecutiveCharsValidator2(value, limit = 2) {
  const consecutiveRegex = new RegExp(`([^\\s])\\1{${limit}}`);
  return !consecutiveRegex.test(value);
}

export function minimumLength(value, minLength) {
  return value.length >= minLength;
}

export function minimumStringLength(value, minLength) {
  return value?.trim()?.length >= minLength;
}

export function optionalMinimumLength(value, minLength = 2) {
  if (value && value.length > 0) {
    return value.length >= minLength;
  }
  return true;
}

export function optionalMinimumMaximumLength(value, minLength = 2, maxLength = 30) {
  if (value && value.length > 0) {
    return value.length >= minLength && value?.length <= maxLength;
  }
  return true;
}

export function sharePercentValidator(value) {
  return value <= 100;
}

export function shareSumValidator(form) {
  const share_arr = form.from[1].value.mwpa_data;

  const shareTotalSum = share_arr.reduce((res, curr) => {
    return res + curr.share_mwpa * 1;
  }, 0);

  return shareTotalSum == 100;
}

export function pincodeValidator(value) {
  const nameRegex = /^[0-9]{6}$/;
  return nameRegex.test(value);
}

// all zero not allowed
export function indianPinCodeValidator(value) {
  const pinRegex = /^(?=.*[1-9])\d{6}$/;
  return pinRegex.test(value);
}

export function monthYearValidator(value) {
  const dateFormat = /^(0[1-9]|1[0-2])\/(20[0-9][0-9])$/;
  return dateFormat.test(value);
}

export function futureMonthYear(value) {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [month, year] = value.split("/");

  if (year > currentYear) {
    return false;
  } else if (year == currentYear && month > currentMonth) {
    return false;
  } else {
    return true;
  }
}

export function dateValidation(value) {
  // Regular expression to match date format "dd/mm/yyyy"
  const dateFormat = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/([0-9][0-9][0-9][0-9])$/;

  if (!dateFormat.test(value)) {
    return false; // Invalid format
  }

  const [day, month, year] = value.split("/").map(Number);
  if (month < 1 || month > 12) {
    return false;
  }
  // Check if it's a leap year
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) {
    return false;
  }

  // Check for February 29th in non-leap years
  if (month === 2 && day === 29 && !isLeapYear) {
    return false;
  }
  return true;
}

//Validate date is not of the future
export function validFutureDate(value) {
  const dateParts = value.split("/").map(Number);
  const day = dateParts[0];
  const month = dateParts[1] - 1;
  const year = dateParts[2];

  const dateValue = new Date(year, month, day);

  const currentDate = new Date();

  // Compare dateValue with currentDate
  return dateValue > currentDate;
}

//max future date (31/12/2099)
export const validMaxFutureDate = (value) => {
  if (!value) {
    return true;
  }

  const dateParts = value.split("/").map(Number);
  const day = dateParts[0];
  const month = dateParts[1] - 1;
  const year = dateParts[2];

  const dateValue = new Date(year, month, day);
  const maxFutureDate = new Date(2099, 11, 31);
  return dateValue <= maxFutureDate;
};
export function isWithinOneYear(dateToCheck) {
  const dateParts = dateToCheck.split("/").map(Number);
  const day = dateParts[0];
  const month = dateParts[1] - 1;
  const year = dateParts[2];
  const dateValue = new Date(year, month, day);

  // Get today's date
  const today = new Date();

  // Calculate one year from today
  const oneYearFromToday = new Date();
  oneYearFromToday.setFullYear(today.getFullYear() - 1);

  // Check if the date is within the range
  return dateValue >= oneYearFromToday;
}

export function compareDates(date1, date2) {
  if (!date1 || !date2) {
    return false;
  }
  const date1Parts = date1?.split("/").map(Number);
  const date2Parts = date2?.split("/").map(Number);

  const day1 = date1Parts[0];
  const month1 = date1Parts[1] - 1;
  const year1 = date1Parts[2];

  const day2 = date2Parts[0];
  const month2 = date2Parts[1] - 1;
  const year2 = date2Parts[2];

  const dateValue1 = new Date(year1, month1, day1);
  const dateValue2 = new Date(year2, month2, day2);

  return dateValue1 <= dateValue2;
}

//Validate date is not of the future
export function isDateFuture(dateString) {
  const today = new Date();
  const [day, month, year] = dateString.split("/").map(Number);
  const inputDate = new Date(year, month - 1, day);

  // Compare inputDate with today's date
  return inputDate < today;
}

//validate  date is within pas number of year
export function isDateWithinPastYears(date, years) {
  if (!date || !years) {
    return false;
  }

  // Parse the input date
  const dateParts = date.split("/").map(Number);
  const day = dateParts[0];
  const month = dateParts[1] - 1; // Months are 0-indexed in JS
  const year = dateParts[2];

  // Convert input date to a Date object
  const inputDate = new Date(year, month, day);
  inputDate.setHours(0, 0, 0, 0);
  // Get the current date and subtract the specified number of years
  // Get the current date
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  // Subtract one year
  const pastDate = new Date();
  pastDate.setFullYear(currentDate.getFullYear() - 1);
  pastDate.setHours(0, 0, 0, 0);

  const isWithinTime = inputDate >= pastDate && inputDate <= currentDate;

  // Check if the input date is between the past date and the current date
  return isWithinTime;
}

export function validateInputCode(input) {
  // Regular expression to match inputs with:
  // - only numbers from 0 to 9
  // - not all zeros
  // - exactly 6 digits
  const regex = /^[1-9][0-9]{5}$/;

  // Check if the input matches the regular expression
  return regex.test(input);
}

export function validateEmail(email) {
  // vaid email with no special characters
  const emailPattern =
    /^(?=.{5,50}$)(?!.*?[._-]{2})[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,12}$/;

  const { blacklistedContacts } = GlobalStore.getSnapshot();
  if (blacklistedContacts?.email.includes(email?.toLowerCase())) {
    return false;
  }
  return emailPattern.test(email);
}

export function optionalValidateEmail(email) {
  // vaid email with no special characters
  if (!email) return true;
  return validateEmail(email);
}

export function validatePAN(value) {
  // Regular expression to match PAN card format
  const panPattern = /^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]{1}$/;
  return panPattern.test(value);
}

export function validateEntityPAN(value) {
  // Regular expression to match Entity PAN card format
  const panPattern = /^[A-Z]{3}[A-OQ-Z][A-Z][0-9]{4}[A-Z]$/;
  return panPattern.test(value);
}

export function validateAadhar(value) {
  // Regular expression to match Aadhaar card format with spaces
  const aadharPattern = /^(\d{4} \d{4} \d{4})$/;
  return aadharPattern.test(value);
}

export function validateIFSC(value) {
  // Regular expression to match IFSC format
  const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  return ifscPattern.test(value);
}

export function validateAadharAndVirtualId(value) {
  const aadharPattern = /^\d{12}$/;
  const virtualIdPattern = /^\d{16}$/;
  return aadharPattern.test(value) || virtualIdPattern.test(value);
}

export function validateAddress(value) {
  const addressPattern = /^(?!.*\s{2})([a-zA-Z0-9.]+(\s?[/\-,]?\s?[a-zA-Z0-9.]+)*)$/;
  return addressPattern.test(value);
}

export function fullAddressValidator(value) {
  return /^(?![\\/,.-]$)(?![\\/,.-]\s)(?!\s*[\\/,.-]$)[a-zA-Z0-9\s\\/,.-]*(?!.*([\\/,.-]){2,})$/.test(value);
}

export function validateAadharSecondary(value) {
  // Regular expression to match Aadhar card format
  const aadharPattern = /^\d{4} \d{4} \d{4} \d{4}$/;
  return aadharPattern.test(value);
}

export function numberToWords(num) {
  const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
  const teens = [
    "",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = ["", "ten", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
  const thousands = ["", "thousand", "lakh", "crore"];

  if (num === 0) return "zero";

  let word = "";

  function getWord(n, index) {
    if (n === 0) return "";

    let str = "";

    if (n > 99) {
      str += ones[Math.floor(n / 100)] + " hundred ";
      n %= 100;
    }

    if (n > 10 && n < 20) {
      str += teens[n - 10] + " ";
    } else {
      str += tens[Math.floor(n / 10)] + " ";
      str += ones[n % 10] + " ";
    }

    str += thousands[index] + " ";

    return str.trim() + " ";
  }

  let index = 0;
  while (num > 0) {
    let chunk = num % 1000;

    if (index === 1) {
      // Handle the thousands place differently for South Asian numbering
      chunk = num % 100;
      num = Math.floor(num / 100);
    } else {
      num = Math.floor(num / 1000);
    }

    if (chunk > 0) {
      word = getWord(chunk, index) + word;
    }

    index++;
  }

  return word.trim() ? word.trim() + " only" : "";
}
// Custom Yup validation function for whole numbers (no decimals)

export function integerValidatorWithNoLeadingZero(value) {
  const regex = /^[1-9][0-9]*$/;
  return regex.test(value);
}
export function integerValidator(value) {
  const regex = /^[0-9]+$/;
  return regex.test(value);
}

// Custom Yup validation function to disallow multiple zeros at the beginning
export function noLeadingZerosValidator(value) {
  const regex = /^(?!0\d)\d*$/;
  return regex.test(value);
}

export function validPassportNumber(value) {
  //Regular expression to match Indian Passport format
  const indianPassportRegex = /^[a-zA-Z0-9]+$/;
  return indianPassportRegex.test(value);
}

export function accNoValidator(value) {
  return 0 < Number(value);
}

export function validateTINPAN(value) {
  // Regular expression to match TIN/PAN card format
  const tinPanPattern = /^[a-zA-Z0-9]+$/;
  return tinPanPattern.test(value);
}

export function validateOnlyOneSpace(value) {
  return !value.includes("  ");
}

export function validEachWordLength(value, length = 10) {
  let isValid = true;
  let names = value.split(" ");
  names.forEach((name) => {
    if (name.length > length) {
      isValid = false;
    }
  });
  return isValid;
}

export function validateEachNameLength(value, length = 30) {
  const names = value.split(" ");
  const state = {
    isValid: true,
    index: "",
    error: "",
  };

  if (names.length > 1) {
    for (let i = 0; i < names.length; i++) {
      if (names[i].length > length) {
        state.isValid = false;
        state.index = i === names.length - 1 ? 1 : 0;
        break; // Exit the loop early if an invalid name is found
      }
    }
  }

  return state;
}

export function validateEachNameMinLength(value, minLength = 2) {
  const names = value.split(" ");
  const state = {
    isValid: true,
    index: "",
  };

  if (names.length > 1) {
    for (let i = 0; i < names.length; i++) {
      if (names[i].length < minLength) {
        state.isValid = false;
        state.index = i === names.length - 1 ? 1 : 0;
        break; // Exit the loop early if an invalid name is found
      }
    }
  }

  return state;
}

export function validateFullName(value) {
  const names = value.trim().split(/\s+/);
  return names.length >= 2;
}

export function removeSpace(value) {
  if (value) return value?.replaceAll(/\s+/g, "");
  return value;
}

export function parseAndValidateDate(dateStr) {
  const dateFormat = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/([0-9]{4})$/;

  if (!dateFormat.test(dateStr)) {
    return null;
  }

  const [day, month, year] = dateStr.split("/").map(Number);

  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = new Date(year, month, 0).getDate();

  if (day > daysInMonth || (month === 2 && day === 29 && !isLeapYear)) {
    return null;
  }
  return new Date(year, month - 1, day);
}

export function calculateDateRange(baseDate, addYears, addMonths = 0) {
  const newDate = new Date(baseDate);
  newDate.setMonth(baseDate.getMonth() + addMonths);
  newDate.setFullYear(baseDate.getFullYear() + addYears);
  return newDate;
}

// Utility function for getting days in a month
export const getDaysInMonth = (month, year) => {
  month = parseInt(month, 10);
  year = parseInt(year, 10);
  if (month === 2) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

// no special characters except (&.,'-()/) or whitespace allowed
export function noSpecialCharactersValidator4(value) {
  const nameRegex = /^[a-zA-Z0-9&.,'\-\s()/]+$/;
  return nameRegex.test(value);
}
